import { useEffect, useState, useMemo } from "react";
import { Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import api from "../../lib/api";
import { formatCurrency } from "../../lib/utils";

/**
 * Renders a compact SVG sparkline showing the unit_price evolution of an
 * ingredient across the last 90 days, from all suppliers.
 */
export function PriceHistorySparkline({ restaurantId, ingredientId, days = 90 }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!restaurantId || !ingredientId) return;
    let cancelled = false;
    setLoading(true);
    api.getIngredientPriceHistory(restaurantId, ingredientId, days)
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [restaurantId, ingredientId, days]);

  const chart = useMemo(() => {
    const snaps = data?.snapshots || [];
    if (snaps.length < 2) return null;
    const W = 320, H = 60, PAD = 4;
    const prices = snaps.map((s) => s.unit_price);
    const min = Math.min(...prices), max = Math.max(...prices);
    const range = Math.max(0.0001, max - min);
    const step = (W - PAD * 2) / (snaps.length - 1);
    const coords = prices.map((p, i) => {
      const x = PAD + i * step;
      const y = H - PAD - ((p - min) / range) * (H - PAD * 2);
      return [x, y];
    });
    const pathD = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
    const areaD = `${pathD} L${coords[coords.length - 1][0]},${H} L${coords[0][0]},${H} Z`;
    return { pathD, areaD, coords, W, H };
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Loader2 className="w-3 h-3 animate-spin" /> Cargando histórico de precios…
      </div>
    );
  }
  const snaps = data?.snapshots || [];
  if (snaps.length < 2) {
    return (
      <div className="text-xs text-muted-foreground italic py-2">
        Aún no hay suficiente histórico de precios. Importa 2+ facturas de este ingrediente para ver la evolución.
      </div>
    );
  }

  const summary = data.summary;
  const deltaPct = summary?.delta_pct ?? 0;
  const trend = deltaPct > 1 ? "up" : deltaPct < -1 ? "down" : "flat";
  const trendColor = trend === "up" ? "#dc2626" : trend === "down" ? "#059669" : "#6b7280";
  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;

  return (
    <div className="space-y-2" data-testid="ingredient-price-sparkline">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Evolución del precio · {days}d</p>
          <p className="text-sm">
            <span className="font-semibold">{formatCurrency(summary.last_price)}</span>
            <span className="text-muted-foreground"> actual · media {formatCurrency(summary.avg_price)} · {summary.sample_count} snapshots</span>
          </p>
        </div>
        <div className="flex items-center gap-1 text-sm font-semibold" style={{ color: trendColor }}>
          <TrendIcon className="w-3.5 h-3.5" />
          {deltaPct > 0 ? "+" : ""}{deltaPct}%
        </div>
      </div>
      <svg width="100%" height={chart.H} viewBox={`0 0 ${chart.W} ${chart.H}`} className="overflow-visible">
        <defs>
          <linearGradient id={`pg_${ingredientId}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={trendColor} stopOpacity="0.25" />
            <stop offset="100%" stopColor={trendColor} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={chart.areaD} fill={`url(#pg_${ingredientId})`} />
        <path d={chart.pathD} fill="none" stroke={trendColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {chart.coords.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i === chart.coords.length - 1 ? 2.5 : 1.5} fill={trendColor} />
        ))}
      </svg>
    </div>
  );
}
